import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2, Save, ArrowLeft, GripVertical } from 'lucide-react';

export default function ITOnboardingChecklist() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/it/onboarding/checklist-template')
      .then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load (need IT permission)'))
      .finally(() => setLoading(false));
  }, []);

  const update = (i, v) => setItems((p) => p.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const add = () => setItems((p) => [...p, '']);

  const save = async () => {
    const clean = items.map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const r = await apiPut('/it/onboarding/checklist-template', { items: clean });
      setItems(r.items || []);
      toast.success('IT onboarding checklist saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed (requires IT Admin / approve permission)');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-2xl space-y-5" data-testid="it-onboarding-checklist-settings">
      <button onClick={() => nav('/app/settings')} className="text-xs text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1.5"><ArrowLeft size={13} /> Back to Settings</button>
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2 flex items-center gap-2"><ListChecks size={13} /> IT</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">IT Onboarding Checklist</h1>
        <p className="text-sm text-slate-600 mt-1">Steps the IT team completes for each new joiner (laptop, email, SIM, access).</p>
      </div>

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`checklist-template-row-${i}`}>
              <GripVertical size={14} className="text-slate-300" />
              <input value={item} onChange={(e) => update(i, e.target.value)} data-testid={`checklist-template-input-${i}`}
                className="flex-1 border border-slate-200 rounded-sm px-3 py-1.5 text-sm focus:border-[#1F7A6E] outline-none" placeholder="Checklist step…" />
              <button onClick={() => remove(i)} data-testid={`checklist-template-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={add} data-testid="checklist-template-add" className="text-xs text-[#1F7A6E] font-bold flex items-center gap-1.5 mt-2"><Plus size={13} /> Add step</button>
        </div>
      )}

      <button onClick={save} disabled={saving} data-testid="checklist-template-save" className="px-5 py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2 disabled:opacity-50"><Save size={14} /> {saving ? 'Saving…' : 'Save Checklist'}</button>
    </div>
  );
}
